import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from 'src/environments/environment';
import { FeaturesBaseServices } from './../../features/services/features-base.services';
import { FeatureComponent } from '../../application/components/feature.component';
import { BusinessServices } from '../services/business.services';
import { BusinessList, BusinessFilterRequest } from '../models/';
import { Application } from 'src/app/_models/feature';

import { BaseServices } from './../../kuba.services';
import { CacheHandlerService } from './../../cached.services';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { Table } from 'primeng/table';
import { Subscription } from 'rxjs';
@Component({
  selector: 'businesses-list',
  templateUrl: 'business-list.component.html',
  styleUrls: ['business-list.component.css'],
  providers: [ConfirmationService],
  standalone: false
})
export class BusinessListComponent implements OnInit, OnDestroy {
  //#region variables
  @ViewChild(FeatureComponent, { static: false })
  featuresComponent: FeatureComponent;
  private readonly notifications = inject(NotificationService);
  @ViewChild('dt') dt: Table;
  IsAdmin: boolean;
  applicationId: number;
  businesses: BusinessList[];
  statuses: SelectItem[];
  months: SelectItem[];
  remoteWindow: Window;
  isChecked = false;
  slider: FormGroup;
  isPortal: boolean;
  additionalData: any;
  loading = false;
  hideNewButton = true;
  totalrecords = 0;
  cols: any[];
  private subscriptions: Subscription[] = [];

  /**
   * Applications whose business list shows the R-mode (Remote Mode) button
   * and the "Navigation" column. Extend this list when a new platform should
   * support R-mode from the business list.
   */
  private readonly rModeEnabledApplications: readonly Application[] = [
    Application.IK,
    Application.BA,
    Application.AHLSELL,
    Application.AGIL,
    Application.SALUTIS
  ];

  /** True when the current platform exposes the R-mode button + Navigation column. */
  get isRModeEnabledApp(): boolean {
    return this.rModeEnabledApplications.includes(this.applicationId);
  }

  // Pagination variables with default alphabetical sorting
  paginationRequest: BusinessFilterRequest = {
    PageNumber: 1,
    PageSize: 10,
    SortColumn: 'CompanyName',    // Default: sort by company name
    SortDirection: 'ASC',         // Default: ascending (A-Z)
    Status: 1                     // Default: show only Active businesses
  };

  totalRecords: number = 0;
  isLoadingData: boolean = false;
  private loadingSubscription: Subscription;
  private isInitialLoad: boolean = true;

  // Filter variables (only the ones used in UI)
  companyNameFilter: string = '';
  connectedToFilter: string = '';
  categoryNameFilter: string = '';
  statusFilter: number | null = 1; // Default to Active (1) instead of null (ALL)
  licenseMonthFilter: string = '';
  
  // Debounce timer for filters
  private filterDebounceTimer: any;
  
  // Export loading state - shows loader in table during export operations
  isExporting: boolean = false;
  //#endregion

  //#region export loading
  /**
   * Handles export loading state from kuba-export component
   * @param isLoading - true when export starts, false when export completes
   */
  onExportLoading(isLoading: boolean): void {
    this.isExporting = isLoading;
  }
  //#endregion

  //#region constructors
  /**
   * constructor
   * @param router {Router}
   * @param route {ActivatedRoute}
   * @param businessServices {BusinessServices}
   * @param confirmationService {ConfirmationService}
   * @param featuresBaseServices {FeaturesBaseServices}
   */
  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private businessServices: BusinessServices,
    private confirmationService: ConfirmationService,
    public featuresBaseServices: FeaturesBaseServices,
    private _fb: FormBuilder,
    private cacheHandlerService: CacheHandlerService,
    private translate: TranslateService
  ) {
    this.subscriptions.push(
      this.translate.stream('SELECT_STATUS_DROP').subscribe(val => {
        this.statuses = [];
        this.statuses.push(
          { label: this.translate.instant('ALL'), value: null },
          { label: val.INACTIVE, value: 2 },
          { label: val.ACTIVE, value: 1 }
        );
      })
    );

    this.subscriptions.push(
      this.translate.stream('SELECT_MONTH').subscribe(val => {
        this.months = [];
        this.months.push(
          { label: this.translate.instant('ALL'), value: null },
          { label: val.JANUARY, value: '01' },
          { label: val.FEBRUARY, value: '02' },
          { label: val.MARCH, value: '03' },
          { label: val.APRIL, value: '04' },
          { label: val.MAY, value: '05' },
          { label: val.JUNE, value: '06' },
          { label: val.JULY, value: '07' },
          { label: val.AUGUST, value: '08' },
          { label: val.SEPTEMBER, value: '09' },
          { label: val.OCTOBER, value: '10' },
          { label: val.NOVEMBER, value: '11' },
          { label: val.DECEMBER, value: '12' }
        );

      })
    );

    this.slider = this._fb.group({
      isChecked: ['']
    });
    this.translate.stream('FILENAME').subscribe(val => {
      this.additionalData = {
        fileName: val.BUSINESS_LIST,
        header: 'Business List',
        businessId: BaseServices.BusinessId,
        cultureInfo: BaseServices.userCultureInfo(),
        columnNames: [
          { title: 'Title', dataKey: 'CompanyName' },
          { title: 'Connected To', dataKey: 'Connectedto' },
          { title: 'Telephone', dataKey: 'Telephone' },
          { title: 'Email', dataKey: 'Email' },
          { title: 'Category', dataKey: 'CategoryName' },
          { title: 'Month', dataKey: 'LicenseMonth' },
          { title: 'Created On', dataKey: 'CreatedOn' },
          { title: 'Modified On', dataKey: 'ModifiedOn' },
          { title: 'Login Date', dataKey: 'LoginDate' },
          { title: 'Login Count', dataKey: 'LoginCount' },
          { title: 'Status', dataKey: 'Status' }
        ]
      };
    });
  }
  //#endregion

  //#region page-events
  /**
   * page load event
   */
  ngOnInit(): void {
    this.applicationId = BaseServices.ApplicationId;
    console.log('ApplicationId', this.applicationId);
    document.querySelector('body').classList.remove('opened');
    if (+BaseServices.roleId === 2 && BaseServices.PortalManager !== true) {
      this.hideNewButton = false;
    }
    
    this.cols = [
      { field: 'CompanyName', header: 'CompanyName' },
      { field: 'Connectedto', header: 'ConnectedTo' },
      { field: 'Telephone', header: 'Telephone' },
      { field: 'CategoryName', header: 'CategoryName' },
      { field: 'LicenseMonth', header: 'Month' },
      { field: 'EmployeeCount', header: 'Employees' },
      { field: 'CreatedOn', header: 'CreatedOn' },
      { field: 'ModifiedOn', header: 'ModifiedOn' },
      { field: 'LoginDate', header: 'LoginDate' },
      { field: 'LoginCount', header: 'LoginCount' }
    ];
    
    this.isPortal = BaseServices.roleId === '2' ? true : false;
    
    // Load initial data with pagination
    this.bindBusinessList(true);
    
    // To hide side menu bar
    document.querySelector('body').classList.remove('opened');
  }

  /**
   * page destroy event
   */
  ngOnDestroy() {
    document.querySelector('body').classList.add('opened');
    
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
    
    // Clear debounce timer
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = null;
    }
    
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }
  //#endregion

  //#region methods
  /**
   * create new business
   */
  createNewBusiness() {
    this.router.navigate(['./wizard']);
  }

  get canImportBusinesses(): boolean {
    return BaseServices.roleId === '1';
  }

  /**
   * delete event
   * @param id {number}
   * @param data {any}
   */
  confirm(id: number, data: any) {
    this.confirmationService.confirm({
      message: this.translate.instant('DELETE_THIS_RECORD'),
      accept: () => {
        this.loading = true;
        this.subscriptions.push(
          this.businessServices.deleteById(id, data).subscribe({
            next: deleted => {
              if (!deleted) {
                this.loading = false;
                this.notifications.error('UI_NOTIFICATIONS.TRY_AGAIN');
                return;
              }
              this.notifications.success('UI_NOTIFICATIONS.DELETED');
              this.businessServices.clearCachedBusinessService();
              this.fetchData();
            },
            error: () => {
              this.loading = false;
            }
          })
        );
      }
    });
  }

  /**
   * status event
   * @param id {number}
   * @param status {any}
   */
  editStatus(id: number, Status: string) {
    const newStatus = Number(Status) === 1 ? '2' : '1';

    const confirmationMessage =
      newStatus === '1'
        ? this.translate.instant('EDIT_ACTIVE_THIS_RECORD')
        : this.translate.instant('EDIT_INACTIVE_THIS_RECORD');

    this.confirmationService.confirm({
      message: confirmationMessage,
      accept: () => {
        this.loading = true; // Show loader
        this.subscriptions.push(
          this.businessServices.statusUpdate(id, newStatus).subscribe({
            next: () => {
              this.notifications.success('UI_NOTIFICATIONS.SAVED');
              this.businessServices.clearCachedBusinessService();
              this.fetchData();
            },
            error:error => {
              console.error('Error updating business status:', error);
              this.loading = false; // Hide loader on error
              
            }
          })
        );
      }
    });
  }

  /**
   * Redirects to the CRM using the organization's number.
   *
   * This method constructs a URL using the organization's number
   * and the base CRM URL from the environment configuration.
   * It then opens this URL in a new tab.
   *
   * @param business - The business object containing organization number.
   */
  cMode(business: any) {
    let baseCRMURL = environment.BASE_CRM_URL;
    const orgNumber = business.OrganizationNumber;
    const routeURL = `${baseCRMURL}ik-redirector/${orgNumber}`;
    // Use the ClientNumber to route to the CRM
    window.open(routeURL, '_blank');
  }
  /**
   * R mode chage
   * @param event {any}
   * @param business {any}
   */
  rModeCheckFeature(business: any) {
    if (this.remoteWindow) {
      this.remoteWindow.close();
    }
    this.cacheHandlerService.clearCachedData();
    let businessSetting = {
      BusinessName: business.CompanyName,
      BusinessLogo: business.BusinessLogo
    };
    sessionStorage.setItem('RModeBusiness', JSON.stringify(businessSetting));
    sessionStorage.setItem('RModeBusinessId', business.Id);
    let windowOrigin = window.location.origin;
    let windowControl =
      'location=yes,width=' +
      screen.availWidth +
      ', height = ' +
      screen.availHeight +
      ', scrollbars = yes, status = yes';
    let baseURL = BaseServices.userBase.BaseUrl;
    this.remoteWindow = window.open(
      windowOrigin +
        '/' +
        baseURL +
        '/app/business/' +
        business.Id +
        '/dashboard',
      'Remote',
      windowControl
    )!;
  }

  /**
   * refresh list and hide loading state
   */
  fetchData() {
    this.bindBusinessList(false);
    // Hide loading state after data refresh
    this.loading = false;
  }

  /**
   * Load business list with current pagination and filter settings
   * Note: All filters automatically reset page number to 1 before calling this method
   */
  bindBusinessList(showInitialLoader: boolean = false) {
    // An immediate refresh also submits any pending text changes.
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = null;
    }

    if (showInitialLoader) {
      this.loading = true;
    }

    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
      this.loadingSubscription = null;
    }

    // Ensure page number is valid (should be 1 when filtering)
    if (this.paginationRequest.PageNumber < 1) {
      this.paginationRequest.PageNumber = 1;
    }

    this.isLoadingData = true;
    this.isInitialLoad = showInitialLoader; // Set initial load flag
    
    console.log('Calling API with request:', this.paginationRequest);
    console.log('ApplicationId:', this.applicationId);
    console.log('IsPortal:', this.isPortal);

    this.loadingSubscription = this.businessServices.getPagedList({ ...this.paginationRequest }).subscribe(
      (response: any) => {
        console.log('API Response:', response);
        console.log('Response type:', typeof response);
        console.log('Response keys:', response ? Object.keys(response) : 'null');
        
        if (response && response.Items && Array.isArray(response.Items)) {
          this.businesses = response.Items;
          this.totalRecords = response.TotalCount || 0;
          this.totalrecords = response.TotalCount || 0;
          this.paginationRequest.PageNumber = response.PageNumber || 1;
          this.paginationRequest.PageSize = response.PageSize || 10;
          console.log('Businesses array:', this.businesses);
          console.log('Total records:', this.totalRecords);
        } else {
          console.log('No items in response or response is null');
          console.log('Response Items:', response?.Items);
          console.log('Response TotalCount:', response?.TotalCount);
          console.log('Response Succeeded:', response?.Succeeded);
          this.businesses = [];
          this.totalRecords = 0;
          this.totalrecords = 0;
        }
        this.isLoadingData = false;
        this.isInitialLoad = false; // Clear initial load flag
        
        // Reset table sort state to match our component state
        this.resetTableSortState();
        
        // Force update PrimeNG table pagination state to match our component state
        this.updateTablePaginationState();
        
        // Hide loading state after successful data load
        this.loading = false;
      },
      error => {
        console.error('Error fetching businesses:', error);
        console.error('Error status:', error.status);
        console.error('Error message:', error.message);
        this.businesses = [];
        this.totalRecords = 0;
        this.totalrecords = 0;
        this.isLoadingData = false;
        // Hide loading state on error
        this.loading = false;
      }
    );
  }

  onPageChange(event: any) {
    if (this.isLoadingData) return;
    
    // Prevent pagination if no data is loaded yet
    if (!this.businesses || this.businesses.length === 0) return;
    
    const newPageNumber = Math.floor(event.first / event.rows) + 1;
    const newPageSize = event.rows;

    // Only update if values actually changed
    if (this.paginationRequest.PageNumber !== newPageNumber || this.paginationRequest.PageSize !== newPageSize) {
      this.paginationRequest.PageNumber = newPageNumber;
      this.paginationRequest.PageSize = newPageSize;
      this.bindBusinessList(false);
    }
  }

  onSort(event: any) {
    console.log('Sort event triggered:', event);
    
    if (this.isLoadingData || this.isInitialLoad) {
      console.log('Sort blocked - loading or initial load');
      return;
    }
    
    // Prevent sorting if no data is loaded yet
    if (!this.businesses || this.businesses.length === 0) {
      console.log('Sort blocked - no data');
      return;
    }

    const sortField = event.field;
    const sortOrder = event.order; // 1 for ASC, -1 for DESC, 0 for no sort
    const newSortDirection = sortOrder === 1 ? 'ASC' : sortOrder === -1 ? 'DESC' : 'ASC';

    console.log('Current sort state:', {
      currentColumn: this.paginationRequest.SortColumn,
      currentDirection: this.paginationRequest.SortDirection,
      newColumn: sortField,
      newDirection: newSortDirection,
      sortOrder: sortOrder
    });

    // Always update sort state when sort event is triggered
    this.paginationRequest.SortColumn = sortField;
    this.paginationRequest.SortDirection = newSortDirection;
    this.paginationRequest.PageNumber = 1;
    
    console.log('Updating sort state and calling API');
    this.bindBusinessList(false);
  }

  /**
   * Handle text filter changes (company name, connected to, category) - resets page number to 1
   */
  onTextFilterChange(filterType: string, value: string) {
    // Cancel stale results immediately, including during the debounce interval.
    this.loadingSubscription?.unsubscribe();

    const searchValue = value.trim() || undefined;
    switch (filterType) {
      case 'companyName':
        this.companyNameFilter = value;
        this.paginationRequest.CompanyName = searchValue;
        break;
      case 'connectedTo':
        this.connectedToFilter = value;
        this.paginationRequest.ConnectedTo = searchValue;
        break;
      case 'categoryName':
        this.categoryNameFilter = value;
        this.paginationRequest.CategoryName = searchValue;
        break;
    }

    // Immediately reset page number to 1 when filter changes
    this.paginationRequest.PageNumber = 1;

    // Clear existing timer
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }

    // Set new timer
    this.filterDebounceTimer = setTimeout(() => {
      // Ensure page number is still 1 (in case it was changed elsewhere)
      this.paginationRequest.PageNumber = 1;
      
      // Immediately update table pagination state to show first page
      this.updateTablePaginationState();
      
      this.bindBusinessList(false);
    }, 300); // 300ms debounce
  }

  /**
   * Handle status filter change - resets page number to 1
   */
  onStatusChanged(event: any) {
    const selectedValue = event?.value !== undefined ? event.value : event;
    
    // Always update when event is triggered (the dropdown change means user wants to filter)
    this.statusFilter = selectedValue;
    this.paginationRequest.Status = selectedValue || undefined;
    this.paginationRequest.PageNumber = 1;  // Reset to first page
    
    // Immediately update table pagination state to show first page
    this.updateTablePaginationState();
    
    this.bindBusinessList(false);
  }

  /**
   * Handle license month filter change - resets page number to 1
   */
  onLicenseMonthChanged(event: any) {
    const selectedValue = event?.value !== undefined ? event.value : event;
    
    // Always update when event is triggered (the dropdown change means user wants to filter)
    this.licenseMonthFilter = selectedValue;
    this.paginationRequest.LicenseMonth = selectedValue || undefined;
    this.paginationRequest.PageNumber = 1;  // Reset to first page
    
    // Immediately update table pagination state to show first page
    this.updateTablePaginationState();
    
    this.bindBusinessList(false);
  }

  /**
   * Check if there are any active filters
   * @returns true if any filter is active
   */
  private hasActiveFilters(): boolean {
    return !!(this.companyNameFilter || 
              this.connectedToFilter || 
              this.categoryNameFilter || 
              this.statusFilter !== null || 
              this.licenseMonthFilter);
  }

  /**
   * Force update PrimeNG table pagination state to match our component state
   * This ensures the UI shows the correct page number
   */
  private updateTablePaginationState(): void {
    if (this.dt) {
      // Calculate the first item index based on current page
      const firstItemIndex = (this.paginationRequest.PageNumber - 1) * this.paginationRequest.PageSize;
      this.dt.first = firstItemIndex;
      
      console.log('Updated table pagination state:', {
        pageNumber: this.paginationRequest.PageNumber,
        pageSize: this.paginationRequest.PageSize,
        firstItemIndex: firstItemIndex,
        tableFirst: this.dt.first
      });
    }
  }

  /**
   * Clear all filters and reset to default state with alphabetical sorting
   */
  clearFilters() {
    // Clear debounce timer
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
      this.filterDebounceTimer = null;
    }

    this.companyNameFilter = '';
    this.connectedToFilter = '';
    this.categoryNameFilter = '';
    this.statusFilter = 1; // Reset to default Active status
    this.licenseMonthFilter = '';

    this.paginationRequest = {
      PageNumber: 1,
      PageSize: 10,
      SortColumn: 'CompanyName',    // Reset to default alphabetical sorting
      SortDirection: 'ASC',         // Reset to default ascending order
      Status: 1                     // Reset to default Active status
    };

    if (this.dt) {
      this.dt.reset();
      // Reset table sort state to default alphabetical sorting
      setTimeout(() => {
        this.dt.sortField = this.paginationRequest.SortColumn;
        this.dt.sortOrder = this.paginationRequest.SortDirection === 'ASC' ? 1 : -1;
      }, 0);
    }

    // Update table pagination state to show first page
    this.updateTablePaginationState();

    this.bindBusinessList(false);
  }

  /**
   * Clear current sorting and reset to default alphabetical sorting
   * Also resets page number to 1 to show results from the beginning
   */
  clearSort() {
    if (this.isLoadingData) return;

    this.paginationRequest.SortColumn = 'CompanyName';    // Reset to default alphabetical sorting
    this.paginationRequest.SortDirection = 'ASC';         // Reset to default ascending order
    this.paginationRequest.PageNumber = 1;                // Reset to first page
    this.paginationRequest.Status = 1;                    // Maintain Active status filter

    if (this.dt) {
      this.dt.sortField = this.paginationRequest.SortColumn;
      this.dt.sortOrder = this.paginationRequest.SortDirection === 'ASC' ? 1 : -1;
    }

    // Update table pagination state to show first page
    this.updateTablePaginationState();

    this.bindBusinessList(false);
  }

  /**
   * Reset the table's sort state to match the component's sort state
   */
  private resetTableSortState() {
    if (this.dt) {
      // Force the table to update its sort state
      setTimeout(() => {
        this.dt.sortField = this.paginationRequest.SortColumn;
        this.dt.sortOrder = this.paginationRequest.SortDirection === 'ASC' ? 1 : -1;
        console.log('Table sort state reset to:', {
          field: this.dt.sortField,
          order: this.dt.sortOrder
        });
      }, 0);
    }
  }

  //#endregion
}
